import { useState, useEffect, useMemo } from "react";
import { 
  Table, 
  Typography, 
  Tag, 
  Alert, 
  Spin, 
  message, 
  Card, 
  Statistic,
  Row,
  Col,
  Input,
  Button,
  Space
} from "antd";
import { 
  DownloadOutlined, 
  ReloadOutlined, 
  WarningOutlined, 
  StockOutlined,
  SearchOutlined
} from "@ant-design/icons";
import axios from "axios";
import { useDebounce } from "use-debounce";
import { CSVLink } from "react-csv";

const { Title, Text } = Typography;

const STOCK_THRESHOLDS = {
  LOW: 10,
  OVER: 100
};

const StockStatusTag = ({ status, value }) => {
  const statusConfig = {
    low: { color: "red", text: "Low Stock" },
    over: { color: "orange", text: "Overstock" },
    normal: { color: "green", text: "Normal" }
  };

  return (
    <Tag 
      color={statusConfig[status].color}
      style={{ 
        fontWeight: 500, 
        borderRadius: 4,
        display: 'flex',
        alignItems: 'center',
        gap: 4
      }}
    >
      {value}
      <span style={{ marginLeft: 4 }}>{statusConfig[status].text}</span>
    </Tag>
  );
};

const RawMaterialsStockManagement = () => {
  const [stockData, setStockData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch] = useDebounce(searchTerm, 300);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchStockData = async () => {
    setLoading(true);
    setError("");
    
    try {
      const [stockRes, receivingRes, vouchersRes] = await Promise.all([
        axios.get("https://agpipefitting.org.et/api/stock"),
        axios.get("https://agpipefitting.org.et/api/receiving-notes"),
        axios.get("https://agpipefitting.org.et/api/store-issue-voucher"),
      ]);

      // Validate API responses
      if (!stockRes.data?.success || !Array.isArray(stockRes.data.data)) {
        throw new Error("Invalid stock data format from API");
      }
      if (!Array.isArray(receivingRes.data)) {
        throw new Error("Invalid receiving notes format from API");
      }
      if (!Array.isArray(vouchersRes.data)) {
        throw new Error("Invalid vouchers format from API");
      }

      const stockItems = stockRes.data.data;
      const receivingNotes = receivingRes.data;
      const vouchers = vouchersRes.data;

      const stockBalance = {};

      // Initialize with current stock values
      stockItems.forEach(item => {
        stockBalance[item.code] = {
          ...item,
          current_stock: Number(item.current_stock),
          unit_price: Number(item.unit_price)
        };
      });

      // Process receiving notes (add stock)
      receivingNotes.forEach(note => {
        note.items.forEach(item => {
          const code = item.item_code;
          if (stockBalance[code]) {
            stockBalance[code].current_stock += Number(item.quantity);
          } else {
            stockBalance[code] = {
              code,
              name: item.item_name,
              current_stock: Number(item.quantity),
              unit_price: Number(item.unit_price)
            };
          }
        });
      });

      // Process issue vouchers (deduct stock)
      vouchers.forEach(voucher => {
        voucher.items.forEach(item => {
          const code = item.item_code;
          if (stockBalance[code]) {
            stockBalance[code].current_stock = Math.max(
              0,
              stockBalance[code].current_stock - Number(item.quantity)
            );
          }
        });
      });

      // Convert to array and add status
      const processedData = Object.values(stockBalance).map(item => ({
        ...item,
        status: getStockStatus(item.current_stock)
      }));

      setStockData(processedData);
      setLastUpdated(new Date());
      message.success("Stock data updated successfully");
    } catch (error) {
      handleFetchError(error);
    } finally {
      setLoading(false);
    }
  };

  const getStockStatus = (quantity) => {
    if (quantity <= STOCK_THRESHOLDS.LOW) return "low";
    if (quantity >= STOCK_THRESHOLDS.OVER) return "over";
    return "normal";
  };

  const handleFetchError = (error) => {
    const errorMessage = error.response?.data?.message || 
      error.message || 
      "Failed to fetch stock data";
    setError(`Error: ${errorMessage} (Status: ${error.response?.status || 'N/A'})`);
    console.error("Stock Fetch Error:", error);
    message.error("Failed to load stock data");
  };

  const filteredData = useMemo(() => 
    stockData.filter(item =>
      Object.values(item).some(val =>
        String(val).toLowerCase().includes(debouncedSearch.toLowerCase())
      )
    ), [stockData, debouncedSearch]
  );

  const stockMetrics = useMemo(() => ({
    totalItems: stockData.length,
    lowStock: stockData.filter(i => i.status === "low").length,
    overStock: stockData.filter(i => i.status === "over").length,
    totalValue: stockData.reduce((sum, i) => sum + (i.current_stock * (i.unit_price || 0)), 0)
  }), [stockData]);

  const columns = [
    { 
      title: "Item Code", 
      dataIndex: "code", 
      key: "code",
      sorter: (a, b) => a.code.localeCompare(b.code)
    },
    { 
      title: "Item Name", 
      dataIndex: "name", 
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name)
    },
    { 
      title: "Current Stock", 
      dataIndex: "current_stock", 
      key: "current_stock",
      sorter: (a, b) => a.current_stock - b.current_stock,
      render: (value, record) => (
        <StockStatusTag status={record.status} value={value} />
      )
    },
    { 
      title: "Unit Price", 
      dataIndex: "unit_price", 
      key: "unit_price",
      render: value => `${Number(value || 0).toFixed(2)}`,
      sorter: (a, b) => a.unit_price - b.unit_price
    },
    { 
      title: "Total Value", 
      key: "total_value",
      render: (_, item) => `${(item.current_stock * (item.unit_price || 0)).toFixed(2)}`,
      sorter: (a, b) => 
        (a.current_stock * a.unit_price) - (b.current_stock * b.unit_price)
    }
  ];

  useEffect(() => {
    fetchStockData();
  }, []);

  return (
    <div style={{ padding: 24, maxWidth: 1440, margin: "0 auto" }}>
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center", 
        marginBottom: 24
      }}>
        <Title level={3} style={{ margin: 0 }}>
          <StockOutlined /> Raw Materials Stock Balances
        </Title>
        <Space>
          <Input
            placeholder="Search items..."
            prefix={<SearchOutlined />}
            style={{ width: 300 }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
          />
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchStockData}
            loading={loading}
          >
            Refresh
          </Button>
          <CSVLink 
            data={filteredData}
            filename={`stock-report-${new Date().toISOString()}.csv`}
          >
            <Button type="primary" icon={<DownloadOutlined />}>
              Export
            </Button>
          </CSVLink>
        </Space>
      </div>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card bordered={false}>
            <Statistic
              title="Total Items"
              value={stockMetrics.totalItems}
              prefix={<StockOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card bordered={false}>
            <Statistic
              title="Low Stock Items"
              value={stockMetrics.lowStock}
              prefix={<WarningOutlined />}
              valueStyle={{ color: "#cf1322" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card bordered={false}>
            <Statistic
              title="Overstock Items"
              value={stockMetrics.overStock}
              prefix={<WarningOutlined />}
              valueStyle={{ color: "#d48806" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card bordered={false}>
            <Statistic
              title="Total Inventory Value"
              value={stockMetrics.totalValue.toFixed(2)}
              prefix="ETB"
            />
          </Card>
        </Col>
      </Row>

      {error ? (
        <Alert
          message={error}
          type="error"
          showIcon
          closable
          action={
            <Button 
              size="small" 
              danger 
              onClick={fetchStockData}
            >
              Retry
            </Button>
          }
          style={{ marginBottom: 24 }}
        />
      ) : (
        <>
          {stockMetrics.lowStock > 0 && (
            <Alert
              message={`Low Stock Alert: ${stockMetrics.lowStock} items below minimum threshold (${STOCK_THRESHOLDS.LOW})`}
              type="warning"
              showIcon
              closable
              style={{ marginBottom: 16 }}
            />
          )}

          {stockMetrics.overStock > 0 && (
            <Alert
              message={`Overstock Alert: ${stockMetrics.overStock} items above maximum threshold (${STOCK_THRESHOLDS.OVER})`}
              type="info"
              showIcon
              closable
              style={{ marginBottom: 16 }}
            />
          )}
        </>
      )}

      <Table
        dataSource={filteredData}
        columns={columns}
        rowKey="code"
        loading={loading}
        pagination={{ 
          pageSize: 10, 
          showSizeChanger: false 
        }}
        scroll={{ x: true }}
        bordered
        size="middle"
        title={() => (
          <Text type="secondary">
            Last Updated: {lastUpdated?.toLocaleString() || "Never"}
          </Text>
        )}
      />
    </div>
  );
};

export default RawMaterialsStockManagement;